import { Component, Input, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { List as ImmutableList } from 'immutable';
import { debounce } from 'lodash-decorators';
import * as constants from '../../constants-types/constants';
import { List } from '../../constants-types/PiList.model';
import { Job } from '../../core-data-model/Job.model';
import { JobService } from '../../core-services/Job.service';
import { AlertOptions } from '../../core-ui/Okapi/OkAlert.component';
import { DateFormatUtility } from '../../core-utilities/DateFormat.utility';
import { Util } from '../../core-utilities/Util';
import { TranslationService } from '../../translations';

@Component({
    selector: 'job-typeahead',
    template: `
        <array-to-single-model
            [(ngModel)]="job"
            (ngModelChange)="processItem($event)"
        >
            <ok-pillbox-with-typeahead
                #childComponent
                [typeaheadList]="jobPossibilities"
                (typeaheadOnTextChange)="typeaheadTextChange($event)"
                [typeaheadPlaceholder]="TR.key('SearchJobs')"
                [pillboxLimit]="1"
                [searchStyling]="true"
                [isDisabled]="isDisabled"
                [isLoadingPossibilities]="loadingJobPossibilities"
            >
                <ng-template #pillbox let-item="item">
                    {{ displayFunction(item) }}
                </ng-template>
                <ng-template #typeahead let-item="item">
                    {{ displayFunction(item) }}
                </ng-template>
            </ok-pillbox-with-typeahead>
        </array-to-single-model>

        @if (showAlert) {
            <ok-alert [alert]="alertType" style="padding-top: 15px;">
            </ok-alert>
        }

        @if (canCreate) {
            <button
                style="margin-top: 10px;"
                class="button button-primary button-md"
                (click)="createNewJob()"
            >
                {{ createAddText() }}
            </button>
        }
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: JobTypeaheadComponent,
            multi: true
        }
    ],
    standalone: false
})
export class JobTypeaheadComponent implements ControlValueAccessor {
    // This is what will be bound to externally
    _job: Job;
    // Value needed by ControlValueAccessor - it is a no-op that is replaced by framework
    propagateChange = (_: any) => {
        /* No change propagated */
    };
    jobPossibilities: List<Job> = ImmutableList();
    textInBox: string;
    loadingJobPossibilities = false;

    @Input()
    canCreate = false;
    @Input()
    isDisabled: boolean;
    @Input()
    getPossibleJobs: (text: string) => Promise<Job[]> = null;
    @Input()
    showAlert = false;
    @Input()
    alertType: AlertOptions = null;

    @Output()
    readonly onCreateNew = new EventEmitter<string>();

    constructor(
        public TR: TranslationService,
        public util: Util,
        public date: DateFormatUtility,
        private jobService: JobService
    ) {}

    typeaheadTextChange(textInBox: string): void {
        if (!textInBox || !textInBox.trim()) {
            this.jobPossibilities = ImmutableList();
            return;
        }
        this.loadingJobPossibilities = true;
        this.filterJobPossibilities(textInBox);
    }

    @debounce(constants.jobSearchDelay)
    private filterJobPossibilities(textInBox: string): void {
        const getJobsFunc = this.getPossibleJobs
            ? () => this.getPossibleJobs(encodeURIComponent(textInBox))
            : () =>
                  this.jobService.getJobsPossibilities(
                      encodeURIComponent(textInBox)
                  );

        if (!this.getPossibleJobs) {
            this.textInBox = textInBox;
        }

        getJobsFunc()
            .then((listOfJobs) => {
                this.jobPossibilities = ImmutableList(listOfJobs);
            })
            .finally(() => {
                this.loadingJobPossibilities = false;
            });
    }

    createNewJob() {
        this.onCreateNew.emit(this.textInBox);
        this.job = null;
        this.textInBox = '';
    }

    createAddText() {
        return this.textInBox && !this.job
            ? this.util.strings.formatString(
                  this.TR.key('CreateX'),
                  this.textInBox
              )
            : this.TR.key('AddNewJob');
    }

    processItem(item: Job | null) {
        if (!item) {
            this.job = null;
        } else {
            this.job = item;
        }
    }

    displayFunction(item: Job) {
        return `${item.title} ${this.date.transformDate(item.lastUpdated)}`;
    }

    /* Meet ControlValueAccessor Interface */
    get job(): Job {
        return this._job;
    }
    set job(val: Job) {
        this._job = val;
        this.propagateChange(this._job);
    }
    // Do NOT propagate changes up here
    writeValue(value: Job): void {
        this._job = value;
    }
    registerOnChange(fn: any): void {
        this.propagateChange = (changed) => fn.call(this, changed);
    }
    registerOnTouched(): void {
        /* No callback function registered OnTouched */
    }
    /** ***************************************/
}
